import React, { useCallback } from 'react';
import { ClubSectionIcon } from './ClubSectionIcons';

/** Strava-style section shell: white card, thin left accent, stroke icon + title + subtitle. */
const SECTION_VARIANTS = {
  performance: {
    shell: 'bg-surface',
    accentBorder: 'border-l-success',
    accentText: 'text-success',
    icon: 'performance',
    subtitle: 'Elo, win rate, and activity',
  },
  upcoming: {
    shell: 'bg-surface',
    accentBorder: 'border-l-info',
    accentText: 'text-info',
    icon: 'upcoming',
    subtitle: 'Next sessions at this club',
  },
  leaderboards: {
    shell: 'bg-surface',
    accentBorder: 'border-l-warning',
    accentText: 'text-warning-dark',
    icon: 'leaderboards',
    subtitle: 'Top players this season',
  },
  matchups: {
    shell: 'bg-surface',
    accentBorder: 'border-l-team2',
    accentText: 'text-team2',
    icon: 'matchups',
    subtitle: 'Head-to-head at this club',
  },
  moments: {
    shell: 'bg-surface',
    accentBorder: 'border-l-primary',
    accentText: 'text-primary',
    icon: 'moments',
    subtitle: 'Highlights from your games',
  },
  sessions: {
    shell: 'bg-surface',
    accentBorder: 'border-l-secondary',
    accentText: 'text-secondary',
    icon: 'sessions',
    subtitle: 'Sessions you played here',
  },
  league: {
    shell: 'bg-surface',
    accentBorder: 'border-l-team1',
    accentText: 'text-team1',
    icon: 'league',
    subtitle: 'Your county league matches',
  },
};

/**
 * Reusable section container for the My Club overview tab.
 *
 * When `onSectionClick` is set, the whole card navigates to a full tab/page.
 * Interactive children must call `e.stopPropagation()` on click.
 */
export function ClubOverviewSection({
  label,
  variant = 'performance',
  subtitle,
  onViewAll,
  onSectionClick,
  viewAllLabel = 'View all',
  children,
  className = '',
}) {
  const isNavigable = Boolean(onSectionClick);
  const tone = SECTION_VARIANTS[variant] ?? SECTION_VARIANTS.performance;
  const displaySubtitle = subtitle ?? tone.subtitle;

  const handleKeyDown = useCallback(
    (event) => {
      if (!onSectionClick) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSectionClick();
      }
    },
    [onSectionClick]
  );

  const handleViewAllClick = useCallback(
    (event) => {
      event.stopPropagation();
      (onViewAll ?? onSectionClick)?.();
    },
    [onViewAll, onSectionClick]
  );

  return (
    <div
      className={`rounded-2xl border border-border-medium shadow-brand-sm overflow-hidden ${tone.shell} border-l-[3px] ${tone.accentBorder} ${
        isNavigable
          ? 'cursor-pointer transition-colors hover:border-border-medium/80 focus-within:ring-2 focus-within:ring-primary/25'
          : ''
      } ${className}`}
      role={isNavigable ? 'button' : undefined}
      tabIndex={isNavigable ? 0 : undefined}
      onClick={isNavigable ? onSectionClick : undefined}
      onKeyDown={isNavigable ? handleKeyDown : undefined}
    >
      <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-3 sm:px-5 sm:pt-5">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-3">
            <span className={`shrink-0 ${tone.accentText}`}>
              <ClubSectionIcon type={tone.icon} />
            </span>
            <p className="min-w-0 truncate text-sm font-semibold text-text-primary leading-tight">
              {label}
            </p>
          </div>
          {displaySubtitle ? (
            <p className="mt-0.5 truncate text-xs leading-snug text-text-muted">
              {displaySubtitle}
            </p>
          ) : null}
        </div>
        {(onViewAll || onSectionClick) && (
          <button
            type="button"
            onClick={handleViewAllClick}
            className="text-sm font-semibold text-primary shrink-0 focus:outline-none focus-visible:underline"
          >
            {viewAllLabel}
          </button>
        )}
      </div>
      <div className="pb-4 px-4 sm:pb-5 sm:px-5">
        {children}
      </div>
    </div>
  );
}
